import React, { useEffect, useMemo, useRef } from 'react';
import { Circle, Group, Label, Tag, Text } from 'react-konva';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearMindmapHighlight } from '../../features/mindmap/mindmapSlice';
import { setZoomLevel } from '../../features/ui/uiSlice';
import { viewportStore } from '../../store/viewportStore';
import { computeFitViewport } from '../../utils/flatPlanCamera';
import {
  canvasUnitsPerMeterFromFloorPlan,
  locationToCanvasPoint,
  machineLocationToCanvas,
} from '../../utils/processMachineMindmap';
import { polygonCentroid } from '../../utils/equipmentGeometry';

const MAX_MARKERS = 800;
/** Mindmap drawer width (w-[32rem]) + right inset — reserved when fitting. */
const MINDMAP_DRAWER_RESERVE_PX = 512 + 12;
/** Extended mindmap drawer (~48rem) + inset. */
const MINDMAP_DRAWER_EXTENDED_RESERVE_PX = 768 + 12;
/** Minimum world span when framing a single machine (~metres). */
const MIN_FOCUS_SPAN_METERS = 6;

type MappedPoint = { id: string; name: string; x: number; y: number };

function resolveMarkerPoint(
  m: { id: string; name: string; locX: number; locY: number },
  params: {
    equipmentInstances: Record<string, { polygon?: Array<{ x: number; y: number }> }>;
    transform: { rawMinX: number; rawMinY: number; invScale: number } | null;
    dxfUnitsPerMeter: number | null | undefined;
    locationBounds: {
      minX: number;
      minY: number;
      maxX: number;
      maxY: number;
    } | null;
    floorBounds: { minX: number; minY: number; maxX: number; maxY: number };
  }
): MappedPoint | null {
  const inst = params.equipmentInstances[m.id];
  if (inst?.polygon?.length) {
    const c = polygonCentroid(inst.polygon);
    return { id: m.id, name: m.name, x: c.x, y: c.y };
  }

  const fromTransform = machineLocationToCanvas(
    m.locX,
    m.locY,
    params.transform,
    params.dxfUnitsPerMeter
  );
  if (fromTransform) {
    return { id: m.id, name: m.name, x: fromTransform.x, y: fromTransform.y };
  }

  if (params.locationBounds) {
    const fallback = locationToCanvasPoint(
      m.locX,
      m.locY,
      params.locationBounds,
      params.floorBounds
    );
    return { id: m.id, name: m.name, x: fallback.x, y: fallback.y };
  }

  return null;
}

/**
 * Floor-plan overlay: draws mindmap-selected machines as highlighted markers
 * so operators can see which tools belong to a process / tool group.
 */
const MindmapHighlightOverlay: React.FC = () => {
  const dispatch = useAppDispatch();
  const highlight = useAppSelector((s) => s.mindmap);
  const floorBounds = useAppSelector((s) => s.floorplan.bounds);
  const transform = useAppSelector((s) => s.floorplan.transform);
  const dxfUnitsPerMeter = useAppSelector((s) => s.floorplan.dxfUnitsPerMeter);
  const canvasUnitsPerMeter = useAppSelector((s) => s.floorplan.canvasUnitsPerMeter);
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);
  const zoom = useAppSelector((s) => s.ui.zoomLevel);
  const workspaceView = useAppSelector((s) => s.ui.workspaceView);
  const drawerExtended = useAppSelector((s) => s.ui.mindmapDrawerExtended);
  const lastFocusKeyRef = useRef<string | null>(null);

  const points = useMemo(() => {
    if (!floorBounds || highlight.markers.length === 0) {
      return [] as MappedPoint[];
    }

    const markers = highlight.markers.slice(0, MAX_MARKERS);
    const projected: MappedPoint[] = [];
    for (const m of markers) {
      const pt = resolveMarkerPoint(m, {
        equipmentInstances,
        transform,
        dxfUnitsPerMeter,
        locationBounds: highlight.locationBounds,
        floorBounds,
      });
      if (pt) projected.push(pt);
    }

    // Render each machine exactly at its projected DB location. We deliberately do NOT apply a
    // cluster-relative drift offset or footprint snapping here: those were estimated from the
    // currently-selected markers, so the same machine's dot moved depending on how many siblings
    // were on screen (e.g. AMTC37#1 shifted between the AMTC37 parent view and its own view).
    return projected;
  }, [
    floorBounds,
    highlight.locationBounds,
    highlight.markers,
    transform,
    dxfUnitsPerMeter,
    equipmentInstances,
  ]);

  // Zoom / pan to highlighted machines when the user selects a node.
  useEffect(() => {
    if (!highlight.nodeId || points.length === 0) {
      if (!highlight.nodeId) lastFocusKeyRef.current = null;
      return;
    }

    const focusKey = `${highlight.nodeId}:${workspaceView}:${drawerExtended ? 'wide' : 'normal'}`;
    if (lastFocusKeyRef.current === focusKey) return;
    lastFocusKeyRef.current = focusKey;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }

    const unitsPerMeter = canvasUnitsPerMeterFromFloorPlan({
      canvasUnitsPerMeter,
      dxfUnitsPerMeter,
      transform,
    });
    const minSpan = unitsPerMeter
      ? Math.max(MIN_FOCUS_SPAN_METERS * unitsPerMeter, 24)
      : 80;
    const spanX = Math.max(maxX - minX, minSpan);
    const spanY = Math.max(maxY - minY, minSpan);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    const vp = viewportStore.getState();
    const drawerReserve =
      workspaceView === 'mindmap'
        ? drawerExtended
          ? MINDMAP_DRAWER_EXTENDED_RESERVE_PX
          : MINDMAP_DRAWER_RESERVE_PX
        : 0;
    const cw = Math.max((vp.canvasWidth || 1200) - drawerReserve, 240);
    const ch = Math.max(vp.canvasHeight || 800, 200);

    const fit = computeFitViewport(
      {
        minX: cx - spanX / 2,
        minY: cy - spanY / 2,
        maxX: cx + spanX / 2,
        maxY: cy + spanY / 2,
      },
      cw,
      ch,
      0.65
    );

    viewportStore.setState(fit);
    dispatch(setZoomLevel(fit.zoom));
  }, [
    highlight.nodeId,
    points,
    workspaceView,
    drawerExtended,
    canvasUnitsPerMeter,
    dxfUnitsPerMeter,
    transform,
    dispatch,
  ]);

  if (!highlight.nodeId || points.length === 0) return null;

  // Compact pin: thin halo + small solid core, scales gently with zoom.
  const coreR = Math.max(1.6, 2.2 / Math.max(zoom, 0.05));
  const haloR = coreR * 1.85;
  const strokeW = Math.max(0.6, 0.9 / Math.max(zoom, 0.05));
  const labelOffset = coreR * 2.2;

  return (
    <Group listening={false}>
      {points.map((p) => (
        <Group key={p.id} x={p.x} y={p.y}>
          <Circle
            radius={haloR}
            fill="rgba(245, 158, 11, 0.12)"
            stroke="#f59e0b"
            strokeWidth={strokeW}
          />
          <Circle radius={coreR} fill="#f59e0b" stroke="#b45309" strokeWidth={strokeW * 0.5} />
        </Group>
      ))}

      {points.length === 1 && (
        <Label
          x={points[0]!.x + labelOffset}
          y={points[0]!.y - labelOffset}
          opacity={0.95}
        >
          <Tag fill="#0f172a" cornerRadius={3 / Math.max(zoom, 0.05)} />
          <Text
            text={points[0]!.name}
            fontSize={9 / Math.max(zoom, 0.05)}
            fill="#fff"
            padding={4 / Math.max(zoom, 0.05)}
          />
        </Label>
      )}
    </Group>
  );
};

export const MindmapHighlightBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const highlight = useAppSelector((s) => s.mindmap);
  const floorBounds = useAppSelector((s) => s.floorplan.bounds);
  const transform = useAppSelector((s) => s.floorplan.transform);
  const dxfUnitsPerMeter = useAppSelector((s) => s.floorplan.dxfUnitsPerMeter);
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);

  const shownOnMap = useMemo(() => {
    if (!floorBounds || highlight.markers.length === 0) return 0;

    let count = 0;
    for (const m of highlight.markers.slice(0, MAX_MARKERS)) {
      const pt = resolveMarkerPoint(m, {
        equipmentInstances,
        transform,
        dxfUnitsPerMeter,
        locationBounds: highlight.locationBounds,
        floorBounds,
      });
      if (pt) count += 1;
    }
    return count;
  }, [
    floorBounds,
    highlight.locationBounds,
    highlight.markers,
    transform,
    dxfUnitsPerMeter,
    equipmentInstances,
  ]);

  if (!highlight.nodeId || highlight.markers.length === 0) return null;

  const hasFloor = !!floorBounds;

  return (
    <div className="pointer-events-auto absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-950 shadow-sm">
      <span className="font-semibold">{highlight.nodeName}</span>
      <span className="text-amber-800/80">
        {highlight.machineCount} machine{highlight.machineCount === 1 ? '' : 's'}
        {hasFloor
          ? ` · ${shownOnMap} shown on map`
          : ' · open a floor plan to see map markers'}
      </span>
      <button
        type="button"
        onClick={() => dispatch(clearMindmapHighlight())}
        className="rounded border border-amber-300 bg-white px-1.5 py-0.5 font-medium text-amber-900 hover:bg-amber-100"
      >
        Clear
      </button>
    </div>
  );
};

export default MindmapHighlightOverlay;
